import { ExportFileButton } from '@framework/common-web/export-file/ExportFileButton.tsx';
import { useExportFile } from '@framework/common-web/export-file/useExportFile.ts';
import {
  getPostAlboRecordCsvMutationOptions,
  getPostAlboRecordPdfMutationOptions,
} from '@framework/common-web/register-records/frontoffice/api/albo-record-controller/albo-record-controller.ts';
import { useGetAlboCfgTipiIdTipoAlboCampi } from '@framework/common-web/register-records/frontoffice/api/configurazione-tipo-albo/configurazione-tipo-albo.ts';
import { type GetAlboRecordParams } from '@framework/common-web/register-records/frontoffice/api/schemas/getAlboRecordParams.ts';
import { mapConfigFieldsToExportFields } from '@framework/common-web/register-records/utils/mapConfigFieldsToExportFields.ts';
import { type ButtonProps } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';

interface IFrontofficeExportRegisterRecordsFileButtonProps {
  type: 'csv' | 'pdf';
  query: GetAlboRecordParams;
  registerRecordTypeId: string;
  registerRecordTypeName: string;
  buttonType?: ButtonProps['type'];
  asMenuItem?: boolean;
}

export function FrontofficeExportRegisterRecordsFileButton(
  props: Readonly<IFrontofficeExportRegisterRecordsFileButtonProps>,
) {
  const {
    type,
    query,
    registerRecordTypeId,
    registerRecordTypeName,
    buttonType,
    asMenuItem,
  } = props;

  const campiQuery = useGetAlboCfgTipiIdTipoAlboCampi(registerRecordTypeId);
  const fields = useMemo(
    () => mapConfigFieldsToExportFields(campiQuery.data?.campi),
    [campiQuery.data?.campi],
  );
  const maxSelectable = campiQuery.data?.limite ?? undefined;

  const sanitizedName = registerRecordTypeName.replace(/[^\p{L}\p{N}]/gu, '_');

  const getMutationOptions =
    type === 'csv'
      ? getPostAlboRecordCsvMutationOptions
      : getPostAlboRecordPdfMutationOptions;

  const { onExport } = useExportFile({
    getMutationOptions,
    downloadApiOptions: {
      queryRequestOptions: { arrayFormat: 'repeat' },
      filename: `${sanitizedName}_${dayjs().format('YYYY-MM-DD_HH_mm_ss')}.${type}`,
    },
    buildVariables: (selectedFields, exportAll) => ({
      data: selectedFields,
      params: {
        ...query,
        idTipoAlbo: registerRecordTypeId,
        ...(exportAll ? { pagina: 0, limite: 1_000_000 } : {}),
      },
    }),
  });

  const fieldsConfiguration = useMemo(
    () => ({
      fields,
      areLoading: campiQuery.isFetching,
      maxSelectables: maxSelectable,
      areRequired: type === 'pdf',
    }),
    [fields, campiQuery.isFetching, maxSelectable, type],
  );

  const pagination = useMemo(
    () => ({ page: Number(query.pagina ?? 0) + 1, pageSize: query.limite }),
    [query.pagina, query.limite],
  );

  return (
    <ExportFileButton
      type={type}
      entity="albi"
      storageDiscriminator={registerRecordTypeId}
      onExport={onExport}
      fieldsConfiguration={fieldsConfiguration}
      pagination={pagination}
      buttonType={buttonType}
      asMenuItem={asMenuItem}
    />
  );
}
